import { z } from "zod";
import { TopolButtonBlockAttributesBasicSchema } from "../template/blocks/buttonBlockSchema";
import { TopolImageAndVideoBlockAttributesBasicSchema } from "../template/blocks/imageAndVideoBlockSchema";
import { TopolTextBlockAttributesBasicSchema } from "../template/blocks/textBlockSchema";

export const TopolBlockOptionsSchema = z.object({
  disabled: z.boolean().optional(),
  disabledText: z.string().optional(),
  hidden: z.boolean().optional(),
  disabledBadge: z.string().optional(),
});

export const TopolBlockTypeSchema = z.enum([
  "text",
  "image",
  "gif",
  "button",
  "divider",
  "spacer",
  "social",
  "video",
  "html",
  "product",
  "dynamic",
  "article",
  "carousel",
  "raw",
  "custom-raw",
  "apiBlock",
  "loop",
  "rating",
]);

export const TopolContentBlockOptionsSchema = z.partialRecord(
  TopolBlockTypeSchema,
  TopolBlockOptionsSchema
);

export type IContentBlockOptions = z.infer<
  typeof TopolContentBlockOptionsSchema
>;

export default TopolContentBlockOptionsSchema;

type textKeys = keyof z.infer<typeof TopolTextBlockAttributesBasicSchema>;
let TextAttributesSchema = z.object({});
Object.keys(TopolTextBlockAttributesBasicSchema.shape).forEach(
  //@ts-expect-error
  (key: textKeys) => {
    TextAttributesSchema = TextAttributesSchema.merge(
      z.object({
        [key]: z
          .union([
            // The `{ value, disabled }` form must be tried first: the basic
            // schemas can be lenient (`.catch(...)`) and would otherwise swallow
            // the object and replace the value with the fallback.
            z.object({
              value: TopolTextBlockAttributesBasicSchema.shape[key],
              disabled: z.boolean().optional(),
              // maybe in zoid
              // validator: z.function().optional(),
              // validatorErrorMessage: z.string().optional(),
            }),
            TopolTextBlockAttributesBasicSchema.shape[key],
          ])
          .optional(),
      })
    );
  }
);
type buttonKeys = keyof z.infer<typeof TopolButtonBlockAttributesBasicSchema>;
let ButtonAttributesSchema = z.object({});
Object.keys(TopolButtonBlockAttributesBasicSchema.shape).forEach(
  //@ts-expect-error
  (key: buttonKeys) => {
    ButtonAttributesSchema = ButtonAttributesSchema.merge(
      z.object({
        [key]: z
          .union([
            // The `{ value, disabled }` form must be tried first: the basic
            // schemas can be lenient (`.catch(...)`) and would otherwise swallow
            // the object and replace the value with the fallback.
            z.object({
              value: TopolButtonBlockAttributesBasicSchema.shape[key],
              disabled: z.boolean().optional(),
              // maybe in zoid
              // validator: z.function().optional(),
              // validatorErrorMessage: z.string().optional(),
            }),
            TopolButtonBlockAttributesBasicSchema.shape[key],
          ])
          .optional(),
      })
    );
  }
);
type imageAndVideoKeys = keyof z.infer<
  typeof TopolImageAndVideoBlockAttributesBasicSchema
>;
let ImageAndVideoAttributesSchema = z.object({});
Object.keys(TopolImageAndVideoBlockAttributesBasicSchema.shape).forEach(
  //@ts-expect-error
  (key: imageAndVideoKeys) => {
    ImageAndVideoAttributesSchema = ImageAndVideoAttributesSchema.merge(
      z.object({
        [key]: z
          .union([
            // The `{ value, disabled }` form must be tried first: the basic
            // schemas can be lenient (`.catch(...)`) and would otherwise swallow
            // the object and replace the value with the fallback.
            z.object({
              value: TopolImageAndVideoBlockAttributesBasicSchema.shape[key],
              disabled: z.boolean().optional(),
              // maybe in zoid
              // validator: z.function().optional(),
              // validatorErrorMessage: z.string().optional(),
            }),
            TopolImageAndVideoBlockAttributesBasicSchema.shape[key],
          ])
          .optional(),
      })
    );
  }
);

export {
  TextAttributesSchema,
  ButtonAttributesSchema,
  ImageAndVideoAttributesSchema,
};

export const TopolCustomBlockMixContentSchema = z.object({
  type: TopolBlockTypeSchema.extract(["button", "video", "text", "image"]),
  attributes: z.any(),
  content: z.string().optional(),
});

export const TopolCustomBlockDataSchema = z.object({
  key: z.string(),
  name: z.string(),
  dialog: z.literal(true).optional(),
  icon: z.string().optional(),
  content: z.string().optional(),
  dialogButtonText: z.string().optional(),
  beta: z.boolean().optional(),
  disabled: z.boolean().optional(),
  disabledBadge: z.string().optional(),
  type: TopolBlockTypeSchema.extract(["button", "video", "text", "image"])
    .or(z.literal("mix"))
    .optional(),
  attributes: z.any().optional(),
  blocks: TopolCustomBlockMixContentSchema.array().optional(),
});

export const TopolCustomBlocksDataSchema = z.array(TopolCustomBlockDataSchema);

export const TopolCustomBlockMixContentWithKeySchema =
  TopolCustomBlockDataSchema.extend({
    key: z.string(),
  });

export const TopolBlockClickedEventSchema = z.object({
  type: TopolBlockTypeSchema,
  customBlockData: TopolCustomBlockDataSchema.or(z.string()).optional(),
});
export type TopolBlockType = z.infer<typeof TopolBlockTypeSchema>;

export type TopolCustomBlockData = z.infer<typeof TopolCustomBlockDataSchema>;
export type TopolCustomBlockMixContent = z.infer<
  typeof TopolCustomBlockMixContentSchema
>;
export type TopolCustomBlockMixContentWithKey = z.infer<
  typeof TopolCustomBlockMixContentWithKeySchema
>;
export type TopolBlockClickedEvent = z.infer<
  typeof TopolBlockClickedEventSchema
>;
